import { Autocomplete, ListItem, ListItemText } from "@mui/material";
import { SxProps } from "@mui/system";
import { Controller, RegisterOptions, useFormContext } from "react-hook-form";
import CommonTextField from "../../commonTextField/CommonTextField";
import AutocompleteChip from "../../customAutocomplete/AutocompleteChip";
import { CustomerCostCenterOption } from "./types";
import useCustomerCostCenterAutocomplete from "./useCustomerCostCenterAutocomplete";

type Props = {
  name: string;
  fullWidth?: boolean;
  isLoading?: boolean;
  inputSx?: SxProps;
  customerCompanyId?: string;
  includeInactive?: boolean;
  workLocationId?: string;
  disabled?: boolean;
  multiple?: boolean;
  limitTags?: number;
  required?: boolean;
  rules?: RegisterOptions;
  error?: boolean;
};

/**
 * Autocomplete field for selecting customer cost center(s).
 *
 * Note: No autocomplete query implemented in backend.
 * Fetches all cost centers for the company and displays them.
 */
const CustomerCostCenterAutocompleteField = ({
  name,
  fullWidth = true,
  isLoading = false,
  inputSx = {},
  customerCompanyId,
  includeInactive = false,
  workLocationId,
  disabled,
  multiple = false,
  limitTags,
  required = false,
  rules,
  error = false,
}: Props) => {
  const { customerCostCenterOptions } = useCustomerCostCenterAutocomplete({
    customerCompanyId,
    includeInactive,
    workLocationId,
  });

  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        ...(required && {
          required: { value: true, message: `${name}_required` },
        }),
        ...rules,
      }}
      render={({ field: { value, onChange } }) => (
        <Autocomplete<CustomerCostCenterOption, boolean, undefined, undefined>
          id="customer-cost-center-autocomplete"
          disabled={disabled}
          fullWidth={fullWidth}
          multiple={multiple}
          value={multiple ? (Array.isArray(value) ? value : []) : value || null}
          loading={isLoading}
          options={customerCostCenterOptions}
          getOptionLabel={(option) => option.name}
          renderOption={(props, option) => (
            <ListItem {...props} key={option.id}>
              <ListItemText>{option.name}</ListItemText>
            </ListItem>
          )}
          limitTags={limitTags}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, newValue) => onChange(newValue)}
          {...(multiple && {
            renderTags: (value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...rest } = getTagProps({ index });
                return (
                  <AutocompleteChip
                    key={key}
                    {...rest}
                    label={option.name || option.id}
                  />
                );
              }),
          })}
          renderInput={(params) => (
            <CommonTextField {...params} sx={inputSx} error={error} />
          )}
        />
      )}
    />
  );
};

export default CustomerCostCenterAutocompleteField;
